import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useBranding } from '../../contexts/BrandingContext';

const {
    FiLinkedin, FiTwitter, FiFacebook, FiYoutube,
    FiGithub, FiExternalLink, FiInstagram
} = FiIcons;

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const [socialLinks, setSocialLinks] = useState([]);
    const [appName, setAppName] = useState('Softvative Expense Tracker');
    const { applicationName, linkedinUrl, twitterUrl, facebookUrl, instagramUrl, youtubeUrl, githubUrl } = useBranding();

    useEffect(() => {
        loadBranding();
        // Listen for branding updates
        window.addEventListener('storage', loadBranding);
        return () => window.removeEventListener('storage', loadBranding);
    }, [applicationName, linkedinUrl, twitterUrl, facebookUrl, instagramUrl, youtubeUrl, githubUrl]);

    const loadBranding = async () => {
        try {
            setAppName(applicationName || 'Softvative Expense Tracker');

            const links = [
                { name: 'LinkedIn', icon: FiLinkedin, url: linkedinUrl },
                { name: 'Twitter', icon: FiTwitter, url: twitterUrl },
                { name: 'Facebook', icon: FiFacebook, url: facebookUrl },
                { name: 'Instagram', icon: FiInstagram, url: instagramUrl },
                { name: 'YouTube', icon: FiYoutube, url: youtubeUrl },
                { name: 'GitHub', icon: FiGithub, url: githubUrl },
            ].filter(link => link.url && link.url.trim() !== '');

            setSocialLinks(links);
        } catch (error) {
            console.error('Failed to load branding for footer', error);
        }
    };

    return (
        <footer className="bg-gray-900 dark:bg-gray-950 text-white border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Company Info */}
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-lg">{appName.charAt(0)}</span>
                            </div>
                            <h3 className="text-xl font-bold">{appName}</h3>
                        </div>
                        <p className="text-gray-400 mb-4 max-w-md">
                            A comprehensive SAAS solution for tracking personal and business expenses with advanced analytics and reporting features.
                        </p>

                        {/* Softvative Logo and Link */}
                        <div className="flex items-center space-x-2 mb-6">
                            <span className="text-sm text-gray-400">Powered by</span>
                            <a
                                href="https://www.softvative.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-2 text-primary-400 hover:text-primary-300 transition-colors"
                            >
                                <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-accent-500 rounded flex items-center justify-center">
                                    <span className="text-white font-bold text-xs">S</span>
                                </div>
                                <span className="font-semibold">Softvative Inc</span>
                                <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                            </a>
                        </div>

                        {/* Social Links */}
                        {socialLinks.length > 0 && (
                            <div className="flex flex-wrap gap-3">
                                {socialLinks.map((social) => (
                                    <a
                                        key={social.name}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all transform hover:-translate-y-1"
                                        title={social.name}
                                    >
                                        <SafeIcon icon={social.icon} className="w-5 h-5" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                        <ul className="space-y-2">
                            <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                            <li><Link to="/features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
                            <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About Us</Link></li>
                            <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4">Legal</h4>
                        <ul className="space-y-2">
                            <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                            <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a></li>
                            <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</a></li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
                    <p className="text-gray-400 text-sm">
                        © {currentYear} Softvative Inc. All rights reserved. Founded in 2008.
                    </p>
                    <p className="text-gray-500 text-xs mt-2 md:mt-0">
                        Developed by: Softvative Team
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;